import React, { useMemo, useCallback } from 'react';
import { Flex, Text, Button, ButtonGroup, Tag, Collapse, Icon } from 'core/components';
import styled from 'styled-components';
import { useHotkeys } from '@blueprintjs/core';
import CodeMirror from 'core/components/CodeMirror';

const ApprovalActionButton = styled(Button)`
  padding: 6px 10px;
`;

export const KaizenToolResultConfirmActions = ({ tool, onApprove, onApproveAndAutoApprove, onReject, disabled }) => {
  // Check if auto-approve is allowed for this tool
  const allowAutoApprove = tool.allow_auto_approve !== false;

  const handleApprove = useCallback(() => {
    if (!disabled) {
      onApprove();
    }
  }, [disabled, onApprove]);

  const handleApproveAndAutoApprove = useCallback(() => {
    if (!disabled) {
      onApproveAndAutoApprove();
    }
  }, [disabled, onApproveAndAutoApprove]);

  const handleReject = useCallback(() => {
    if (!disabled) {
      onReject();
    }
  }, [disabled, onReject]);

  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);

  const toggleDetails = useCallback(() => {
    setIsDetailsOpen((prev) => !prev);
  }, []);

  // Memoize hotkeys array to avoid infinitely re-binding hotkeys
  // Conditionally include auto-approve hotkey based on allowAutoApprove
  const hotkeys = useMemo(() => {
    const baseHotkeys = [
      {
        combo: '1',
        label: 'Approve tool once',
        onKeyDown: handleApprove,
        preventDefault: true
      }
    ];

    if (allowAutoApprove) {
      baseHotkeys.push({
        combo: '2',
        label: 'Approve tool for conversation',
        onKeyDown: handleApproveAndAutoApprove,
        preventDefault: true
      });
    }

    baseHotkeys.push({
      combo: allowAutoApprove ? '3' : '2',
      label: 'Reject tool usage',
      onKeyDown: handleReject,
      preventDefault: true
    });

    return baseHotkeys;
  }, [handleApprove, handleApproveAndAutoApprove, handleReject, allowAutoApprove]);

  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap={1}
      mt={1}
      flex={1}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    >
      <Text small>
        The <strong>{tool.tool_name}</strong> tool requires your approval before execution.
      </Text>
      <ButtonGroup vertical fill style={{ width: '100%', maxWidth: '250px' }}>
        <ApprovalActionButton
          fill
          fontWeight="bold"
          text={
            <Flex alignItems="center" gap={1}>
              <Tag round>1</Tag>
              Approve
            </Flex>
          }
          textAlign="left"
          onClick={onApprove}
          disabled={disabled}
        />
        {allowAutoApprove && (
          <ApprovalActionButton
            fill
            text={
              <Flex alignItems="center" gap={1}>
                <Tag round>2</Tag>
                Approve for this conversation
              </Flex>
            }
            textAlign="left"
            onClick={onApproveAndAutoApprove}
            disabled={disabled}
          />
        )}
        <ApprovalActionButton
          fill
          text={
            <Flex alignItems="center" gap={1}>
              <Tag round>{allowAutoApprove ? '3' : '2'}</Tag>
              Reject
            </Flex>
          }
          textAlign="left"
          onClick={onReject}
          disabled={disabled}
        />
      </ButtonGroup>
      <Flex alignItems="center" gap="4px" mt={1} style={{ cursor: 'pointer' }} onClick={toggleDetails}>
        <Icon icon={isDetailsOpen ? 'caret-right' : 'caret-down'} size={12} color="muted" />
        <Text small muted>
          {isDetailsOpen ? 'Hide' : 'View'} tool details
        </Text>
      </Flex>
      <Collapse isOpen={isDetailsOpen}>
        <CodeMirror
          mode="javascript"
          value={JSON.stringify(tool.query_object, null, 2)}
          options={{
            readOnly: true
          }}
        />
      </Collapse>
    </Flex>
  );
};
